import { useEffect, useMemo, useState } from "react";
import { AuthInterceptor } from "../services/AuthInterceptor.Service";
import Spinnerr from "../components/Commaon/Spinner";
import Toaster from "../components/Commaon/Toaster";
import { formatDate } from "../utils/date";

const toDateInput = (date) => date.toISOString().slice(0, 10);

const getDefaultFilters = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 27);
  return {
    fromDate: toDateInput(start),
    toDate: toDateInput(end),
    orderStatus: "",
    paymentStatus: "",
  };
};

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatNumber = (value) =>
  new Intl.NumberFormat("en-IN").format(Number(value || 0));

const growthClass = (value) => {
  if (value > 0) return "text-success";
  if (value < 0) return "text-danger";
  return "text-muted";
};

const Dashboard = () => {
  const [analytics, setAnalytics] = useState(null);
  const [filters, setFilters] = useState(getDefaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(getDefaultFilters);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedFilters.fromDate) params.set("fromDate", appliedFilters.fromDate);
    if (appliedFilters.toDate) params.set("toDate", appliedFilters.toDate);
    if (appliedFilters.orderStatus) params.set("orderStatus", appliedFilters.orderStatus);
    if (appliedFilters.paymentStatus) params.set("paymentStatus", appliedFilters.paymentStatus);
    return params.toString();
  }, [appliedFilters]);

  useEffect(() => {
    const loadAnalytics = async () => {
      setLoading(true);
      try {
        const response = await AuthInterceptor(`/admin/dashboard/analytics?${queryString}`, {
          method: "GET",
        });

        if (response?.data?.s === 200) {
          setAnalytics(response.data.r);
        } else {
          setAnalytics(null);
          setSnack({
            notification: {
              message: response?.data?.m || "Failed to load dashboard analytics",
              type: "error",
            },
          });
        }
      } catch (error) {
        setSnack({
          notification: { message: "Failed to load dashboard analytics", type: "error" },
        });
      } finally {
        setLoading(false);
      }
    };

    loadAnalytics();
  }, [queryString]);

  const applyPreset = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - (days - 1));
    const nextFilters = {
      ...filters,
      fromDate: toDateInput(start),
      toDate: toDateInput(end),
    };
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setAppliedFilters(filters);
  };

  const cards = analytics?.cards || {};
  const orderSummary = analytics?.orderSummary || {};
  const maxDailyOrders = Math.max(
    1,
    ...(analytics?.dailyOrders || []).map((item) => Number(item.orders || 0))
  );

  const kpis = [
    {
      label: "Orders",
      value: formatNumber(cards.ordersLastPeriod),
      detail: `${orderSummary.orderGrowthPercent || 0}% vs previous period`,
      detailClass: growthClass(orderSummary.orderGrowthPercent || 0),
    },
    {
      label: "Revenue",
      value: formatCurrency(cards.revenueLastPeriod),
      detail: `${orderSummary.revenueGrowthPercent || 0}% vs previous period`,
      detailClass: growthClass(orderSummary.revenueGrowthPercent || 0),
    },
    {
      label: "Total Users",
      value: formatNumber(cards.totalUsers),
      detail: `${formatNumber(cards.newUsers)} new in selected range`,
    },
    {
      label: "Avg. Order Value",
      value: formatCurrency(cards.averageOrderValue),
      detail: `${formatNumber(orderSummary.paidOrders)} paid orders`,
    },
    {
      label: "Lab Technicians",
      value: formatNumber(cards.totalLabTechnicians),
      detail: `${formatNumber(cards.pendingLabApprovals)} approvals pending`,
    },
    {
      label: "Catalog",
      value: `${formatNumber(cards.activeTests)} / ${formatNumber(cards.activePackages)}`,
      detail: "Active tests / active packages",
    },
    {
      label: "Prescriptions",
      value: formatNumber(cards.prescriptionsInRange),
      detail: "Uploaded in selected range",
    },
    {
      label: "Notifications",
      value: formatNumber(cards.notificationsInRange),
      detail: "Created in selected range",
    },
  ];

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="dashboard-page">
        <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
          <div>
            <h2 className="mb-1">Dashboard</h2>
            <div className="text-muted small">
              Analytics from {formatDate(analytics?.range?.fromDate)} to {formatDate(analytics?.range?.toDate)}
            </div>
          </div>

          <div className="d-flex gap-2">
            <button className="btn btn-outline-custom btn-sm" onClick={() => applyPreset(7)}>
              7 days
            </button>
            <button className="btn btn-outline-custom btn-sm" onClick={() => applyPreset(28)}>
              28 days
            </button>
            <button className="btn btn-outline-custom btn-sm" onClick={() => applyPreset(90)}>
              90 days
            </button>
          </div>
        </div>

        <form className="analytics-filter-bar" onSubmit={applyFilters}>
          <div>
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-control"
              value={filters.fromDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, fromDate: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-control"
              value={filters.toDate}
              onChange={(e) => setFilters((prev) => ({ ...prev, toDate: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">Order Status</label>
            <select
              className="form-select"
              value={filters.orderStatus}
              onChange={(e) => setFilters((prev) => ({ ...prev, orderStatus: e.target.value }))}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Processing">Processing</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="form-label">Payment Status</label>
            <select
              className="form-select"
              value={filters.paymentStatus}
              onChange={(e) => setFilters((prev) => ({ ...prev, paymentStatus: e.target.value }))}
            >
              <option value="">All</option>
              <option value="Pending">Pending</option>
              <option value="Completed">Completed</option>
              <option value="Failed">Failed</option>
              <option value="Refunded">Refunded</option>
            </select>
          </div>
          <button className="btn btn-outline-custom" type="submit">
            Apply
          </button>
        </form>

        <div className="analytics-kpi-grid">
          {kpis.map((item) => (
            <div className="analytics-card" key={item.label}>
              <div className="text-muted small">{item.label}</div>
              <div className="analytics-card-value">{item.value}</div>
              <div className={`small ${item.detailClass || "text-muted"}`}>{item.detail}</div>
            </div>
          ))}
        </div>

        <div className="row g-3 mt-1">
          <div className="col-lg-7">
            <div className="analytics-panel">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Orders Trend</h5>
                <span className="text-muted small">Daily count</span>
              </div>
              <div className="daily-bars">
                {(analytics?.dailyOrders || []).length ? (
                  analytics.dailyOrders.map((item) => (
                    <div className="daily-bar-row" key={item.date}>
                      <div className="daily-bar-date">{formatDate(item.date)}</div>
                      <div className="daily-bar-track">
                        <div
                          className="daily-bar-fill"
                          style={{ width: `${Math.max(4, (Number(item.orders || 0) / maxDailyOrders) * 100)}%` }}
                        />
                      </div>
                      <div className="daily-bar-count">{formatNumber(item.orders)}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-muted py-4 text-center">No order data for selected filters</div>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-5">
            <div className="analytics-panel h-100">
              <h5 className="mb-3">Order Summary</h5>
              <SummaryList title="Order Status" rows={analytics?.statusBreakdown || []} />
              <div className="border-top my-3" />
              <SummaryList title="Payment Status" rows={analytics?.paymentBreakdown || []} />
            </div>
          </div>
        </div>

        <div className="analytics-panel mt-3">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Recent Orders</h5>
          </div>
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th>Order Number</th>
                  <th>Amount</th>
                  <th>Payment</th>
                  <th>Order</th>
                  <th>Delivery</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {(analytics?.recentOrders || []).length ? (
                  analytics.recentOrders.map((order) => (
                    <tr key={order._id}>
                      <td>{order.orderNumber || "-"}</td>
                      <td>{formatCurrency(order.finalAmount)}</td>
                      <td>{order.paymentStatus}</td>
                      <td>{order.orderStatus}</td>
                      <td>{order.deliveryStatus}</td>
                      <td>{formatDate(order.createdAt)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center text-muted py-4">
                      No recent orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
};

const SummaryList = ({ title, rows }) => (
  <div>
    <div className="text-muted small mb-2">{title}</div>
    {rows.length ? (
      rows.map((row) => (
        <div className="summary-row" key={row.label}>
          <span>{row.label}</span>
          <strong>{formatNumber(row.count)}</strong>
        </div>
      ))
    ) : (
      <div className="text-muted small">No data</div>
    )}
  </div>
);

export default Dashboard;
